import { Feature } from '@/utils/constant';
import { hasFeature } from '@/services/featureService';
import { isProcessBillingVendorEligible } from '@/pages/caseManagement/processBilling/processBillingVendorUtils';
import { getProcessBillingVendor, sendProcessBillingVendorAffidavit } from '@/services/processBillingVendorService';
import type { WorkOrderGetByIdResponse } from '@/types/workOrder.types';
import { convertISODateToMMDDYYYY, convertISOToTime, displayFullName, formatCurrency } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentListDialog } from '@/components/DocumentListDialog';
import type { DocumentListType, MediaBaseResponse } from '@/types/types';
import { SendAffidavitConfirmDialog } from '@/pages/caseManagement/processBilling/SendAffidavitConfirmDialog';

export const ProcessBillingVendorPePanel = ({ workOrder }: { workOrder: WorkOrderGetByIdResponse | undefined }) => {
  const navigate = useNavigate();
  const workOrderId = workOrder?.workOrderId;
  const clientId = workOrder?.service?.client?.clientId ?? null;

  const flagOn = hasFeature(Feature.PROCESS_BILLING_VENDOR) || workOrder?.service?.type === 'PROCESS_SERVICE';
  const eligible = Boolean(workOrder && isProcessBillingVendorEligible(workOrder));

  const q = useQuery({
    queryKey: ['processBillingVendor', workOrderId],
    queryFn: () => getProcessBillingVendor(workOrderId!),
    enabled: Boolean(workOrderId) && flagOn && eligible,
    retry: false,
  });

  const entry = q.isSuccess ? (q.data?.entry ?? null) : null;

  const allAffidavits = useMemo(() => entry?.proofOfService ?? [], [entry]);
  const allDocuments = useMemo<DocumentListType[]>(
    () =>
      allAffidavits.map((doc) => ({
        name: doc.name,
        media: { mediaId: doc.mediaId } as MediaBaseResponse,
        affidavitSendDocumentId: doc.processBillingVendorDocumentId,
      })),
    [allAffidavits]
  );

  const [openAffidavitDialog, setOpenAffidavitDialog] = useState(false);
  const [pendingSendAffidavitDocumentId, setPendingSendAffidavitDocumentId] = useState<string | null>(null);

  if (!flagOn || !eligible || !workOrderId) return null;

  const goView = () => navigate(`/process/workorder/${workOrderId}/process-billing-vendor`);

  if (q.isLoading) return null;

  /** No in-panel empty state — use Billing tab ⋮ menu ("Add Vendor Billing") so new WOs are not cluttered. */
  if (!entry || !q.data) return null;

  const updatedAt = q.data.updatedAt
    ? `${convertISODateToMMDDYYYY(q.data.updatedAt)} ${convertISOToTime(q.data.updatedAt)}`
    : '—';
  const updatedByName = displayFullName(q.data.updatedBy ?? {});
  const updatedLine = `Updated: ${updatedAt}${updatedByName && updatedByName !== '-' ? ` by ${updatedByName}` : ''}`;

  return (
    <div className="px-6 pt-2 pb-4 flex flex-col gap-3">
      <div
        className="flex flex-row gap-3 rounded-lg bg-[#F5F5F5] p-4 cursor-pointer items-start"
        onClick={goView}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            goView();
          }
        }}
      >
        <FileText className="w-6 h-6 text-primary shrink-0" />
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <span className="text-sm font-semibold text-primary">Billing Entry - Vendor Payment</span>
          <span className="text-xs text-placeholder">{updatedLine}</span>
          <div className="text-xs">
            <span className="text-placeholder">Amount Due: </span>
            <span className="text-primary font-medium">{formatCurrency(entry.amountDue)}</span>
          </div>
          <div className="text-xs">
            <span className="text-placeholder">Invoice #: </span>
            <span className="text-primary font-medium">{entry.vendorInvoiceNumber}</span>
          </div>
        </div>
      </div>

      {allAffidavits.length > 0 ? (
        <div
          className="flex flex-row gap-3 rounded-lg bg-[#F5F5F5] p-4 items-start cursor-pointer"
          onClick={() => setOpenAffidavitDialog(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpenAffidavitDialog(true);
            }
          }}
        >
          <FileText className="w-6 h-6 text-primary shrink-0" />
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <span className="text-sm font-semibold text-primary">Vendor Billing - Document</span>
            <span className="text-xs text-placeholder">{updatedLine}</span>
          </div>
        </div>
      ) : null}

      {openAffidavitDialog && allDocuments.length > 0 ? (
        <DocumentListDialog
          documents={allDocuments}
          activeIndex={0}
          onCloseClick={() => setOpenAffidavitDialog(false)}
          onRequestSendAffidavit={(id) => setPendingSendAffidavitDocumentId(id)}
        />
      ) : null}
      {pendingSendAffidavitDocumentId ? (
        <SendAffidavitConfirmDialog
          workOrderId={workOrderId}
          documentIds={[pendingSendAffidavitDocumentId]}
          clientId={clientId}
          sendFn={sendProcessBillingVendorAffidavit}
          onClose={() => setPendingSendAffidavitDocumentId(null)}
        />
      ) : null}
    </div>
  );
};
